import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import FormField from './FormField';
import EditFieldModal from '../Modals/EditFieldModal';
import EditButtonModal from '../Modals/EditButtonModal';
import { useNavigate } from 'react-router-dom';
import AlertCustomization from './AlertCustomization';
import Swal from 'sweetalert2';
import { Pencil } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import ReportDeliveryModal from '../Modals/ReportDeliveryModal';

const validateInput = (value, type) => {
  switch (type) {
    case 'text':
      return /^[a-zA-Z\s]*$/.test(value); // Only letters and spaces
    case 'email':
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value); // Basic email validation
    case 'phone':
      return /^[0-9]{10}$/.test(value); // 10 digit phone number
    case 'number':
      return /^[0-9]*$/.test(value); // Only numbers
    default:
      return true; // No validation
  }
};

const FormBuilder = ({ isResponseMode }) => {
  const navigate = useNavigate();
  const [fields, setFields] = useState([
    {
      id: 'default-name',
      type: 'input',
      label: 'Nombre',
      placeholder: 'Nombre',
      required: true,
      options: [],
      validationType: 'text',
    },
    {
      id: 'default-lastname',
      type: 'input',
      label: 'Apellido',
      placeholder: 'Apellido',
      required: true,
      options: [],
      validationType: 'text',
    },
  ]);
  const [submitButton, setSubmitButton] = useState({
    text: 'Enviar',
    backgroundColor: '#2563eb',
    textColor: '#ffffff',
    borderRadius: 'rounded',
    padding: 'p-4',
    width: 'w-full',
  });
  const [editingField, setEditingField] = useState(null);
  const [formName, setFormName] = useState('');
  const [formId, setFormId] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userOrg, setUserOrg] = useState(null);
  const [headerImage, setHeaderImage] = useState('');
  const [headerImagePosition, setHeaderImagePosition] = useState('center'); // 'left', 'center', 'right'
  const [headerImageSize, setHeaderImageSize] = useState('medium'); // 'small', 'medium', 'large'
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    formName: '',
    fields: {},
    formNameColor: '#000000',
    headerImage: {
      url: '',
      position: 'center',
      size: 'medium',
    },
    alertSettings: {
      responseType: 'alert',
      redirectUrl: '',
      title: '¡Gracias por tu respuesta!',
      message: 'Tus respuestas han sido registradas exitosamente.',
      type: 'success',
      backgroundColor: '#ffffff',
      textColor: '#000000',
    },
    submitButton: {
      text: 'Enviar',
      backgroundColor: '#2563eb',
      textColor: '#ffffff',
      borderRadius: 'rounded',
      padding: 'p-4',
      width: 'w-full',
      size: 'medium',
      position: 'center',
    },
    reportDelivery: {
      type: null,
      config: {
        url: '',
        host: '',
        port: '',
        username: '',
        password: '',
        directory: '/',
      },
      active: false,
    },
    socialLinks: {
      whatsapp: '',
      facebook: '',
      instagram: '',
      linkedin: '',
      twitter: '',
    },
  });
  const [buttonSettings, setButtonSettings] = useState({
    text: 'Enviar',
    backgroundColor: '#2563eb',
    textColor: '#ffffff',
    borderRadius: 'rounded',
    padding: 'p-4',
    width: 'w-full',
    size: 'medium',
    position: 'center',
  });
  const [userId, setUserId] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [org, setOrg] = useState('');
  const [alertSettings, setAlertSettings] = useState({
    responseType: 'alert', // o 'redirect'
    redirectUrl: '',
    title: '¡Gracias por tu respuesta!',
    message: 'Tus respuestas han sido registradas exitosamente.',
    type: 'success',
    backgroundColor: '#ffffff',
    textColor: '#000000',
  });
  const [isAlertCustomizationOpen, setIsAlertCustomizationOpen] =
    useState(false);
  const [buttonCustomizationOpen, setButtonCustomizationOpen] = useState(false);
  const [formNameColor, setFormNameColor] = useState('#000000');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [formNameFontSize, setFormNameFontSize] = useState('16px');
  const [formNameFontFamily, setFormNameFontFamily] = useState('Arial');
  const [isReportDeliveryOpen, setIsReportDeliveryOpen] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    setTimeout(() => {
      if (formData?.submitButton) {
        setButtonSettings(formData.submitButton);
      }
    }, 100);
  }, [formData]);

  useEffect(() => {
    if (formData?.submitButton) {
      console.log('submitButton recibido:', formData.submitButton); // 🔍 Verifica los valores
      setButtonSettings(prev => ({
        ...prev,
        size: formData.submitButton.size || 'medium',
        position: formData.submitButton.position || 'center',
        borderRadius: formData.submitButton.borderRadius || 'rounded',
      }));
    }
  }, [formData.submitButton]);

  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      ['link'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['clean'],
    ],
  };

  const formats = ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];

  const handleWordPressUpload = async file => {
    try {
      setIsUploadingImage(true);
      const formData = new FormData();
      formData.append('file', file);

      const auth = btoa('AppCenter:mzJzZX9NWbaOg5yoQroJ6ZIs');

      const response = await fetch(
        'https://www.yesbpo.com/wp-json/wp/v2/media',
        {
          method: 'POST',
          headers: {
            Authorization: `Basic ${auth}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('WordPress upload response:', data);

      // Set the image URL in state
      setHeaderImage(data.source_url);
      console.log('Header image set to:', data.source_url);

      Swal.fire({
        title: '¡Éxito!',
        text: 'Imagen subida correctamente',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      Swal.fire({
        title: 'Error',
        text: 'Error al subir la imagen: ' + error.message,
        icon: 'error',
      });
    } finally {
      setIsUploadingImage(false);
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('No hay sesión activa');
          navigate('/login');
          return;
        }

        console.log('Enviando pedido con token:', token);

        const response = await fetch(
          process.env.REACT_APP_URL_BASE_BACKEND + '/api/user/profile',
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        console.log('Respuesta status:', response.status);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.error || 'Error al obtener información del usuario'
          );
        }

        const userData = await response.json();
        console.log('User data received:', userData); // Solo un log

        if (!userData.org) {
          throw new Error('Usuario no tiene organización asignada');
        }

        setUserOrg(userData.org);
        setUserId(userData._id); // Guardar el userId
      } catch (error) {
        console.error('Error fetching user data:', error);
        setError(error.message || 'Error al obtener información del usuario');
        if (
          error.message.includes('Token') ||
          error.message.includes('sesión')
        ) {
          navigate('/login');
        }
      }
    };

    fetchUserData();
  }, [navigate]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get('id');
    console.log('ID del formulario:', id); // Verifica que el ID se esté obteniendo correctamente
    setFormId(id);
  }, []);

  // Añade este useEffect después de las declaraciones de estado
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
    .block a {
      color: #2563eb !important;
      text-decoration: underline !important;
      cursor: pointer !important;
    }
    .block a:hover {
      color: #1d4ed8 !important;
    }
  `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    const fetchFormData = async () => {
      if (!formId) return;

      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await fetch(
          process.env.REACT_APP_URL_BASE_BACKEND + `/forms/${formId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error('Error al obtener el formulario');
        }

        const data = await response.json();
        console.log('Datos del formulario recibidos:', data);
        setFormData(data);
        setFormName(data.formName);
        setFormNameColor(data.formNameColor || '#000000');
        // Manejo específico de la imagen
        if (data.headerImage) {
          console.log('Datos de imagen encontrados:', data.headerImage);

          if (typeof data.headerImage === 'string') {
            // Si headerImage es solo una URL
            setHeaderImage(data.headerImage);
            setHeaderImagePosition('center');
            setHeaderImageSize('medium');
          } else if (
            typeof data.headerImage === 'object' &&
            data.headerImage.url
          ) {
            // Si headerImage es un objeto con propiedades
            setHeaderImage(data.headerImage.url);
            setHeaderImagePosition(data.headerImage.position || 'center');
            setHeaderImageSize(data.headerImage.size || 'medium');
          }
        }
        setFields(data.fields);
      } catch (error) {
        console.error('Error fetching form data:', error);
        setError('Error al cargar el formulario');
      } finally {
        setLoading(false);
      }
    };

    if (formId) {
      fetchFormData();
    }
  }, [formId]);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        process.env.REACT_APP_URL_BASE_BACKEND + '/organizations',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      setOrganizations(data);
    } catch (error) {
      console.error('Error fetching organizations', error);
    }
  };

  const fieldTypes = [
    { id: 'input', type: 'input', label: 'Entrada Texto' },
    { id: 'textarea', type: 'textarea', label: 'Entrada Texto Descriptivo' },
    { id: 'select', type: 'select', label: 'Desplegable' },
    { id: 'radio', type: 'radio', label: 'Opción Unica' },
    { id: 'checkbox', type: 'checkbox', label: 'Opción Multiple' },
    { id: 'date', type: 'date', label: 'Fecha' },
    { id: 'time', type: 'time', label: 'Hora' },
    { id: 'file', type: 'file', label: 'Adjunto' },
    { id: 'text', type: 'text', label: 'Texto' },
    { id: 'multiselect', type: 'multiselect', label: 'Selector Múltiple' },
    {
      id: 'ticketGenerator',
      type: 'ticketGenerator',
      label: 'Generador de ID',
    },
    { id: 'ticketSelector', type: 'ticketSelector', label: 'Selector de ID' },
  ];
  const sanitizeConfig = {
    ADD_ATTR: ['target', 'rel', 'style'],
    ADD_TAGS: ['a'],
    ALLOWED_TAGS: ['p', 'a', 'strong', 'em', 'span', 'br', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
    transformTags: {
      a: (tagName, attribs) => ({
        tagName,
        attribs: {
          ...attribs,
          target: '_blank',
          rel: 'noopener noreferrer',
          style: 'color: #2563eb; text-decoration: underline; cursor: pointer;',
        },
      }),
    },
  };

  const createNewField = type => {
    const fieldType = fieldTypes.find(field => field.type === type);
    const isQuestion = type === 'text'; // El campo de tipo 'text' es la pregunta
    const isTicketGenerator = type === 'ticketGenerator';
    const isTicketSelector = type === 'ticketSelector';
    return {
      id: Date.now().toString(),
      type,
      label: isQuestion
        ? 'Pregunta'
        : isTicketGenerator
          ? 'Número de Ticket'
          : isTicketSelector
            ? 'Seleccionar Ticket'
            : `Nuevo ${fieldType.label}`, // Use label from fieldTypes
      placeholder: '',
      required: false,
      options: [], // Siempre un array vacío, incluso para campos que no usan options
      isQuestion, // Indicamos si el campo es la pregunta
      isTicketGenerator,
      isTicketSelector,
      validationType:
        type === 'email' ? 'email' : type === 'number' ? 'number' : 'none', // Add validation type for number
      maxLength: type === 'number' ? 10 : undefined, // Add maxLength for number
      dateRestriction: type === 'date' ? 'none' : undefined,
      minDate: null,
      maxDate: null,
    };
  };

  const addField = (type, index = fields.length) => {
    const newField = createNewField(type);
    const newFields = [...fields];
    newFields.splice(index, 0, newField);
    setFields(newFields);
  };

  const editField = updatedField => {
    setFields(
      fields.map(field => (field.id === updatedField.id ? updatedField : field))
    );
    setEditingField(null);
  };

  const deleteField = id => {
    setFields(fields.filter(field => field.id !== id));
  };

  const onDragEnd = result => {
    const { source, destination } = result;

    // Si no hay destino válido, salimos
    if (!destination) return;

    // Si el origen es fieldTypes y el destino es fields
    if (
      source.droppableId === 'fieldTypes' &&
      destination.droppableId === 'fields'
    ) {
      const fieldType = fieldTypes.find(
        field => field.id === result.draggableId
      );
      if (fieldType) {
        addField(fieldType.type, destination.index);
      }
      return;
    }

    // Si es un reordenamiento dentro de fields
    if (
      source.droppableId === 'fields' &&
      destination.droppableId === 'fields'
    ) {
      const newFields = Array.from(fields);
      const [removed] = newFields.splice(source.index, 1);
      newFields.splice(destination.index, 0, removed);
      setFields(newFields);
    }
  };

  const validationPatterns = {
    letters: /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]*$/,
    numbers: /^\d*$/,
    alphanumeric: /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s]*$/,
    email: /^[^\s@]*@?[^\s@]*\.?[^\s@]*$/,
    phone: /^[0-9-+\s()]*$/,
  };

  const handleInputChange = (e, field) => {
    const value = e.target.value;

    if (field.validationType && field.validationType !== 'none') {
      const pattern = validationPatterns[field.validationType];
      if (pattern && !pattern.test(value)) {
        return; // Si no pasa la validación, no actualiza el valor
      }
    }

    // Actualiza el estado del campo
    setFields(prevFields =>
      prevFields.map(f => (f.id === field.id ? { ...f, value: value } : f))
    );
  };

  const saveForm = async () => {
    if (!formName.trim()) {
      alert('Debes ingresar un nombre para el formulario');
      return;
    }

    if (!userOrg) {
      alert('No se ha podido determinar la org del usuario');
      return;
    }

    // Validar la URL si el tipo de respuesta es redirect
    if (
      formData.alertSettings.responseType === 'redirect' &&
      !formData.alertSettings.redirectUrl
    ) {
      alert('Debes ingresar una URL de redirección válida');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      alert('No hay sesion activa');
      navigate('/login');
      return;
    }

    try {
      console.log('Color antes de enviar:', formData.formNameColor);
      setLoading(true);

      // Verificar que tenemos la información de la imagen
      // Log the image data before saving
      console.log('Header Image Data before save:', {
        url: headerImage,
        position: headerImagePosition,
        size: headerImageSize,
      });

      const processedFields = fields.map(field => {
        console.log(
          `Guardando campo: ${field.label}, Restricción de fecha: ${field.dateRestriction}`
        );
        const processed = { ...field };

        if (field.type === 'date') {
          processed.dateRestriction = field.dateRestriction || 'none';
          if (field.dateRestriction === 'range') {
            processed.minDate = field.minDate || null;
            processed.maxDate = field.maxDate || null;
          } else {
            // Para otras restricciones, eliminar minDate y maxDate
            delete processed.minDate;
            delete processed.maxDate;
          }
        }

        switch (field.borderRadius) {
          case '':
            processed.borderRadius = 0;
            break;
          case 'rounded':
            processed.borderRadius = 4;
            break;
          case 'rounded-md':
            processed.borderRadius = 6;
            break;
          case 'rounded-lg':
            processed.borderRadius = 8;
            break;
          default:
            processed.borderRadius = 0;
        }

        switch (field.padding) {
          case 'p-2':
            processed.padding = 2;
            break;
          case 'p-4':
            processed.padding = 4;
            break;
          case 'p-6':
            processed.padding = 6;
            break;
          default:
            processed.padding = 4;
        }

        return processed;
      });

      console.log('Datos de los campos antes de guardar:', fields);
      console.log('submitButton antes de enviar:', buttonSettings);

      const cleanSubmitButton = submitButton => {
        return {
          text: submitButton?.text || buttonSettings.text || 'Enviar',
          backgroundColor:
            submitButton?.backgroundColor ||
            buttonSettings.backgroundColor ||
            '#2563eb',
          textColor:
            submitButton?.textColor || buttonSettings.textColor || '#ffffff',
          borderRadius:
            submitButton?.borderRadius ||
            buttonSettings.borderRadius ||
            'rounded',
          padding: submitButton?.padding || buttonSettings.padding || 'p-4',
          width: submitButton?.width || buttonSettings.width || 'w-full',
          size: submitButton?.size || buttonSettings.size || 'medium',
          position:
            submitButton?.position || buttonSettings.position || 'center',
        };
      };

      const formDataToSend = {
        formName,
        formNameColor,
        headerImage: headerImage
          ? {
            url: headerImage,
            position: headerImagePosition || 'center',
            size: headerImageSize || 'medium',
          }
          : null,
        submitButton,
        fields: processedFields,
        org: userOrg,
        alertSettings: {
          ...formData.alertSettings,
          // Asegurarse de que solo se incluyan los campos relevantes según el tipo
          ...(formData.alertSettings.responseType === 'alert'
            ? {
              title: formData.alertSettings.title,
              message: formData.alertSettings.message,
              type: formData.alertSettings.type,
              backgroundColor: formData.alertSettings.backgroundColor,
              textColor: formData.alertSettings.textColor,
            }
            : {
              redirectUrl: formData.alertSettings.redirectUrl,
            }),
        },
        submitButton: cleanSubmitButton(buttonSettings),
        reportDelivery: formData.reportDelivery || {
          type: null,
          config: {
            url: '',
            host: '',
            port: '',
            username: '',
            password: '',
            directory: '/',
          },
          active: false,
        },
        footer: '© 2023 Empresa. Todos los derechos reservados.', // Footer predeterminado
      };

      console.log('Enviando datos:', formDataToSend);
      console.log(
        'Formulario antes de enviar:',
        JSON.stringify(formDataToSend, null, 2)
      );
      console.log(
        'Enviando formulario:',
        JSON.stringify(
          {
            formName,
            fields,
            alertSettings: formData.alertSettings,
            submitButton: buttonSettings, // Asegúrate de que se envíen correctamente
          },
          null,
          2
        )
      );

      const url = formId
        ? process.env.REACT_APP_URL_BASE_BACKEND + `/api/forms/${formId}` // http://localhost:3001 , https://yes4.tupropuesta.co
        : process.env.REACT_APP_URL_BASE_BACKEND + `/api/forms`;

      const response = await fetch(url, {
        method: formId ? 'PATCH' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formDataToSend),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al guardar el formulario');
      }

      const savedForm = await response.json();
      console.log('Formulario guardado:', savedForm);
      
      // Mostrar mensaje de éxito
      setSuccessMessage(formId ? 'El formulario se ha actualizado correctamente' : 'El formulario se ha creado correctamente');
      setShowSuccessAlert(true);

      // Ocultar la alerta después de 3 segundos
      setTimeout(() => {
        setShowSuccessAlert(false);
        navigate('/myforms');
      }, 3000);

    } catch (error) {
      console.error('Error:', error);
      alert(error.message || 'Hubo un error al guardar el formulario');
    } finally {
      setLoading(false);
    }
  };

  const handleButtonChange = (key, value) => {
    setButtonSettings(prev => {
      const newState = { ...prev, [key]: value };
      console.log('Nuevo estado del botón:', newState); // 🔍 Verificar si React lo está actualizando
      return newState;
    });
  };

  const handlePreview = () => {
    // Guardar formulario primero
    Swal.fire({
      title: '¿Guardar cambios?',
      text: 'Para ver la vista previa, primero necesitamos guardar los cambios actuales.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Guardar y previsualizar',
      cancelButtonText: 'Cancelar',
    }).then(async result => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          await saveForm();

          // Configuración del popup
          const width = 800;
          const height = 600;
          const left = (window.innerWidth - width) / 2;
          const top = (window.innerHeight - height) / 2;
          const features = `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes`;

          // Abrir en popup en lugar de una nueva pestaña
          window.open(`/view-form/${formId}`, 'PreviewPopup', features);
        } catch (error) {
          console.error('Error saving form:', error);
          Swal.fire({
            title: 'Error',
            text: 'No se pudo guardar el formulario para la vista previa',
            icon: 'error',
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Add this helper function at the top level of your component
  const stripHtml = html => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  const renderFormPreview = () => {
    return fields.map((field, index) => (
      <Draggable key={field.id} draggableId={field.id} index={index}>
        {provided => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className="flex items-center justify-between bg-base-100 p-2 mb-2 border rounded shadow"
          >
            <div className="flex-1">
              <label
                className="block mb-2 font-bold"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(field.label),
                }}
              />
              {field.type === 'input' && (
                <input
                  type="text"
                  placeholder={field.placeholder}
                  className="p-2 border rounded w-full"
                  maxLength={field.maxLength || undefined}
                  onKeyDown={e => {
                    if (
                      field.validationType === 'numbers' &&
                      !/^\d$/.test(e.key) &&
                      ![
                        'Backspace',
                        'Delete',
                        'ArrowLeft',
                        'ArrowRight',
                        'Tab',
                      ].includes(e.key)
                    ) {
                      e.preventDefault();
                    }
                  }}
                  onInput={e => {
                    const value = e.target.value;

                    // Check maxLength
                    if (field.maxLength && value.length > field.maxLength) {
                      e.target.value = value.slice(0, field.maxLength);
                      return;
                    }

                    // Apply validation patterns
                    if (
                      field.validationType &&
                      field.validationType !== 'none'
                    ) {
                      const pattern = validationPatterns[field.validationType];
                      if (pattern && !pattern.test(value)) {
                        e.target.value = value.slice(0, -1);
                        return;
                      }
                    }

                    handleInputChange(e, field);
                  }}
                  value={field.value || ''}
                  disabled={isResponseMode}
                  style={{
                    backgroundColor: field.backgroundColor,
                    color: field.labelColor,
                    borderColor: field.borderColor,
                    borderRadius:
                      field.borderRadius === 'rounded'
                        ? '0.375rem'
                        : field.borderRadius === 'rounded-md'
                          ? '0.5rem'
                          : field.borderRadius === 'rounded-lg'
                            ? '0.75rem'
                            : '0',
                    padding:
                      field.padding === 'p-2'
                        ? '0.5rem'
                        : field.padding === 'p-4'
                          ? '1rem'
                          : field.padding === 'p-6'
                            ? '1.5rem'
                            : '1rem',
                  }}
                />
              )}
              {field.type === 'textarea' && (
                <textarea
                  placeholder={field.placeholder}
                  className="p-2 border rounded w-full"
                  disabled
                />
              )}
              {field.type === 'select' && (
                <select className="p-2 border rounded w-full" disabled>
                  <option value="">Seleccionar</option>
                  {field.options.map((option, idx) => (
                    <option key={idx} value={stripHtml(option)}>
                      {stripHtml(option)}
                    </option>
                  ))}
                </select>
              )}
              {field.type === 'radio' && (
                <div>
                  {field.options.map((option, idx) => (
                    <label key={idx} className="block flex items-center gap-2">
                      <input
                        type="radio"
                        name={field.id}
                        value={option}
                        disabled
                      />
                      <span
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(option, sanitizeConfig),
                        }}
                      />
                    </label>
                  ))}
                </div>
              )}

              {field.type === 'checkbox' && (
                <div>
                  {field.options.map((option, idx) => (
                    <label key={idx} className="block flex items-center gap-2">
                      <input
                        type="checkbox"
                        name={field.id}
                        value={option}
                        disabled
                      />
                      <span
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(option, sanitizeConfig),
                        }}
                      />
                    </label>
                  ))}
                </div>
              )}
              {field.type === 'date' && (
                <input
                  type="date"
                  className="p-2 border rounded w-full"
                  min={
                    field.dateRestriction === 'future'
                      ? new Date().toISOString().split('T')[0]
                      : field.dateRestriction === 'range' && field.minDate
                        ? new Date(field.minDate).toISOString().split('T')[0]
                        : undefined
                  }
                  max={
                    field.dateRestriction === 'past'
                      ? new Date().toISOString().split('T')[0]
                      : field.dateRestriction === 'range' && field.maxDate
                        ? new Date(field.maxDate).toISOString().split('T')[0]
                        : undefined
                  }
                  style={{
                    backgroundColor: field.backgroundColor,
                    color: field.labelColor,
                    borderColor: field.borderColor,
                    borderRadius:
                      field.borderRadius === 'rounded'
                        ? '0.375rem'
                        : field.borderRadius === 'rounded-md'
                          ? '0.5rem'
                          : field.borderRadius === 'rounded-lg'
                            ? '0.75rem'
                            : '0',
                    padding:
                      field.padding === 'p-2'
                        ? '0.5rem'
                        : field.padding === 'p-4'
                          ? '1rem'
                          : field.padding === 'p-6'
                            ? '1.5rem'
                            : '1rem',
                  }}
                />
              )}
              {field.type === 'time' && (
                <input
                  type="time"
                  className="p-2 border rounded w-full"
                  disabled
                />
              )}
              {field.type === 'file' && (
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  className="p-2 border rounded w-full"
                  disabled
                />
              )}
              {field.type === 'text' && (
                <p className="p-2 border rounded w-full">{field.label}</p>
              )}
              {field.type === 'email' && (
                <input
                  type="email"
                  placeholder={field.placeholder}
                  className="p-2 border rounded w-full"
                  disabled
                />
              )}
              {field.type === 'number' && (
                <input
                  type="text" // Usamos type="text" para poder usar maxLength
                  placeholder={field.placeholder}
                  className="p-2 border rounded w-full"
                  maxLength={field.maxLength || ''}
                  onInput={e => {
                    const value = e.target.value;
                    // Solo permite números
                    if (!/^\d*$/.test(value)) {
                      e.target.value = value.slice(0, -1);
                    }
                    // Limita la cantidad de caracteres
                    if (value.length > field.maxLength) {
                      e.target.value = value.slice(0, field.maxLength);
                    }
                  }}
                  disabled={isResponseMode}
                />
              )}
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => setEditingField(field)}
                className="p-2 hover:bg-base-200 rounded-full transition-colors"
                title="Editar campo"
              >
                <Pencil size={20} />
              </button>
              <button
                type="button"
                onClick={() => deleteField(field.id)}
                className="p-2 hover:bg-base-200 rounded-full transition-colors"
                title="Eliminar campo"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M6 2a1 1 0 00-1 1v1H3a1 1 0 100 2h1v9a2 2 0 002 2h8a2 2 0 002-2V6h1a1 1 0 100-2h-2V3a1 1 0 00-1-1H6zm3 4a1 1 0 112 0v7a1 1 0 11-2 0V6z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </Draggable>
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-lg">Cargando...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-600 bg-red-100 rounded">
        <h2 className="text-lg font-bold mb-2">Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="p-4 bg-base-100 text-base-content">
      {showSuccessAlert && (
        <div role="alert" className="alert alert-success mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 shrink-0 stroke-current"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>{successMessage}</span>
        </div>
      )}
      <h1 className="text-center text-2xl font-bold mb-6 text-base-content">
        Constructor de Formularios
      </h1>
      {formId && (
        <div>
          <select
            className="grow"
            value={userOrg}
            onChange={e => {
              setUserOrg(e.target.value);
              console.log(
                '🔍 Nueva organización seleccionada:',
                e.target.value
              );
            }}
            required
          >
            <option value="">Seleccione una organización</option>
            {Array.isArray(organizations) ? (
              organizations.map(org => (
                <option key={org._id} value={org.name}>
                  {org.name}
                </option>
              ))
            ) : (
              <option disabled>Cargando organizaciones...</option>
            )}
          </select>
        </div>
      )}
      <br />
      <div className="mb-6">
        <h2 className="block mb-2 text-lg font-bold">Imagen de Encabezado</h2>
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <div className="relative">
              <input
                type="file"
                accept="image/*"
                onChange={e => {
                  const file = e.target.files[0];
                  if (file) {
                    if (file.size > 5 * 1024 * 1024) {
                      // 5MB limit
                      Swal.fire({
                        title: 'Error',
                        text: 'El archivo es demasiado grande. Máximo 5MB permitido.',
                        icon: 'error',
                      });
                      return;
                    }
                    handleWordPressUpload(file);
                  }
                }}
                className="file-input file-input-bordered w-full max-w-xs"
                disabled={isUploadingImage}
              />
              {isUploadingImage && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded">
                  <div className="loading loading-spinner loading-lg text-primary"></div>
                </div>
              )}
            </div>
            <select
              value={headerImagePosition}
              onChange={e => setHeaderImagePosition(e.target.value)}
              className="select select-bordered"
              disabled={isUploadingImage}
            >
              <option value="left">Izquierda</option>
              <option value="center">Centro</option>
              <option value="right">Derecha</option>
            </select>
            <select
              value={headerImageSize}
              onChange={e => setHeaderImageSize(e.target.value)}
              className="select select-bordered"
              disabled={isUploadingImage}
            >
              <option value="small">Pequeña</option>
              <option value="medium">Mediana</option>
              <option value="large">Grande</option>
            </select>
          </div>

          {headerImage && (
            <div
              className="w-full"
              style={{
                textAlign: headerImagePosition,
              }}
            >
              <img
                src={headerImage}
                alt="Header"
                style={{
                  display: 'inline-block',
                  maxWidth:
                    headerImageSize === 'small'
                      ? '200px'
                      : headerImageSize === 'medium'
                        ? '400px'
                        : '600px',
                  height: 'auto',
                }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="mb-6 relative">
        <h1 className="block mb-2 text-lg font-bold text-base-content">
          Titulo del Formulario
        </h1>

        <div className="flex flex-col gap-2">
          <ReactQuill
            value={formName}
            onChange={setFormName}
            modules={modules}
            formats={formats}
            className="bg-base-200 rounded"
            style={{
              fontSize: formNameFontSize,
              fontFamily: formNameFontFamily,
              color: formData.formNameColor,
            }}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowColorPicker(!showColorPicker)}
              className="p-2 hover:bg-base-200 rounded-full transition-colors"
              title="Cambiar color del texto"
            >
              <Pencil size={20} />
            </button>
            {showColorPicker && (
              <div className="absolute right-0 mb-2 z-50 bg-base-100 shadow-lg rounded-lg">
                <input
                  type="color"
                  value={formData.formNameColor}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      formNameColor: e.target.value,
                    })
                  }
                  className="w-8 h-8 cursor-pointer"
                />
              </div>
            )}
          </div>
        </div>

        <DragDropContext onDragEnd={onDragEnd}>
          <div className="flex">
            <Droppable droppableId="fieldTypes" isDropDisabled={true}>
              {provided => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="w-1/4 p-4 bg-base-200"
                >
                  <h2 className="mb-4 text-base-content">Tipos de Campos</h2>
                  {fieldTypes.map((field, index) => (
                    <Draggable
                      key={field.id}
                      draggableId={field.id}
                      index={index}
                    >
                      {provided => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="p-2 mb-2 bg-base-100 border rounded shadow cursor-move text-base-content"
                        >
                          {field.label}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>

            <Droppable droppableId="fields">
              {provided => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="flex-1 p-4 bg-base-200 min-h-[200px]"
                >
                  <h2 className="mb-4 text-base-content">Área de Trabajo</h2>
                  {renderFormPreview()}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        </DragDropContext>

        <div className="mt-4 mb-4">
          <button
            type="button"
            onClick={() => setIsAlertCustomizationOpen(true)}
            className="px-4 py-2 bg-black text-primary-content rounded hover:bg-primary-focus flex items-center space-x-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                clipRule="evenodd"
              />
            </svg>
            <span>Configuración del formulario</span>
          </button>
        </div>

        <div className="mt-4 mb-4">
          <button
            type="button"
            onClick={() => setButtonCustomizationOpen(true)}
            className="px-4 py-2 bg-black text-primary-content rounded hover:bg-primary-focus flex items-center space-x-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                clipRule="evenodd"
              />
            </svg>
            <span>Configuración de boton</span>
          </button>
        </div>

        <div className="mt-4 mb-4">
          <button
            type="button"
            onClick={() => setIsReportDeliveryOpen(true)}
            className="px-4 py-2 bg-black text-primary-content rounded hover:bg-primary-focus flex items-center space-x-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z"
                clipRule="evenodd"
              />
            </svg>
            <span>Configuración de envío de reportes</span>
          </button>
        </div>

        <EditButtonModal
          buttonSettings={buttonSettings}
          onButtonSettingsChange={setButtonSettings}
          isOpen={buttonCustomizationOpen}
          onClose={() => setButtonCustomizationOpen(false)}
          handleButtonChange={handleButtonChange}
        />

        <AlertCustomization
          alertSettings={formData.alertSettings}
          onAlertSettingsChange={newSettings =>
            setFormData({
              ...formData,
              alertSettings: newSettings,
            })
          }
          isOpen={isAlertCustomizationOpen}
          onClose={() => setIsAlertCustomizationOpen(false)}
        />

        <ReportDeliveryModal
          reportDelivery={formData.reportDelivery}
          onReportDeliveryChange={newSettings =>
            setFormData({
              ...formData,
              reportDelivery: newSettings,
            })
          }
          isOpen={isReportDeliveryOpen}
          onClose={() => setIsReportDeliveryOpen(false)}
        />

        {editingField && (
          <EditFieldModal
            field={editingField}
            onSave={editField}
            onClose={() => setEditingField(null)}
          />
        )}
      </div>

      <div className="flex gap-4 mt-4">
        {formId && (
          <button
            onClick={handlePreview}
            disabled={loading}
            className="p-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 flex-1"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
              <path
                fillRule="evenodd"
                d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                clipRule="evenodd"
              />
            </svg>
            Vista Previa
          </button>
        )}
      </div>

      <div className="flex gap-4 mt-4">
        <button
          onClick={saveForm}
          disabled={loading}
          className={`p-2 bg-black text-primary-content rounded hover:bg-primary-focus flex-1 ${loading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
        >
          {loading
            ? 'Guardando...'
            : formId
              ? 'Actualizar Formulario'
              : 'Crear Formulario'}
        </button>
      </div>
    </div>
  );
};

export default FormBuilder;
